import { Patient, MedicalRecord, MedicalRecordItem } from '../types';
import { patients as seedPatients, medicalRecords as seedRecords } from './mockData';

const LS_KEY_PATIENTS = 'pertrain.patients.v1';
const LS_KEY_RECORDS = 'pertrain.records.v1';

function safeParse<T>(raw: string | null, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

// Initialize localStorage from seeds if empty
(function init() {
  if (typeof window === 'undefined') return;
  const p = localStorage.getItem(LS_KEY_PATIENTS);
  const r = localStorage.getItem(LS_KEY_RECORDS);
  if (!p) localStorage.setItem(LS_KEY_PATIENTS, JSON.stringify(seedPatients));
  if (!r) localStorage.setItem(LS_KEY_RECORDS, JSON.stringify(seedRecords));
})();

export function getPatients(): Patient[] {
  if (typeof window === 'undefined') return seedPatients;
  return safeParse<Patient[]>(localStorage.getItem(LS_KEY_PATIENTS), seedPatients);
}

export function getPatientByIdStore(id: string): Patient | undefined {
  return getPatients().find(p => p.id === id);
}

export function savePatients(all: Patient[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LS_KEY_PATIENTS, JSON.stringify(all));
}

export function upsertPatient(patient: Patient) {
  const all = getPatients();
  const idx = all.findIndex(p => p.id === patient.id);
  if (idx >= 0) all[idx] = patient; else all.push(patient);
  savePatients(all);
}

export function getRecords(): MedicalRecord[] {
  if (typeof window === 'undefined') return seedRecords;
  return safeParse<MedicalRecord[]>(localStorage.getItem(LS_KEY_RECORDS), seedRecords);
}

export function saveRecords(all: MedicalRecord[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LS_KEY_RECORDS, JSON.stringify(all));
}

export function getMedicalRecordByPatientIdStore(patientId: string): MedicalRecord | undefined {
  return getRecords().find(r => r.patientId === patientId);
}

export function ensureMedicalRecord(patientId: string): MedicalRecord {
  const all = getRecords();
  let rec = all.find(r => r.patientId === patientId);
  if (!rec) {
    rec = { id: `record-${Date.now()}`, patientId, records: [] };
    all.push(rec);
    saveRecords(all);
  }
  return rec;
}

export function addRecord(patientId: string, item: Omit<MedicalRecordItem, 'id'>): MedicalRecordItem {
  const all = getRecords();
  let rec = all.find(r => r.patientId === patientId);
  if (!rec) {
    rec = { id: `record-${Date.now()}`, patientId, records: [] };
    all.push(rec);
  }
  const newItem: MedicalRecordItem = { id: `item-${Date.now()}`, ...item };
  rec.records.push(newItem);
  saveRecords(all);
  return newItem;
}

export function updateRecord(patientId: string, item: MedicalRecordItem) {
  const all = getRecords();
  const rec = all.find(r => r.patientId === patientId);
  if (!rec) return;
  const idx = rec.records.findIndex(it => it.id === item.id);
  if (idx >= 0) {
    rec.records[idx] = item;
    saveRecords(all);
  }
}

export function deleteRecord(patientId: string, itemId: string) {
  const all = getRecords();
  const rec = all.find(r => r.patientId === patientId);
  if (!rec) return;
  rec.records = rec.records.filter(it => it.id !== itemId);
  saveRecords(all);
}
